import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/button/button';
import { Checkbox } from '@/components/checkbox/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Spinner } from '@/components/spinner/spinner';
import { getListUserQueryOptions } from '@/features/users/api/get-list-user.api';

export interface VoucherMentorSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
}

export default function VoucherMentorSelector({
  value,
  onChange,
}: VoucherMentorSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const mentorsQuery = useQuery({
    ...getListUserQueryOptions({ limit: 100, search, type: 'mentor' }),
    enabled: open,
  });
  const mentors = mentorsQuery.data?.result.data ?? [];

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  };

  return (
    <div className="grid gap-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map(id => {
            const mentor = mentors.find(m => m.id_user === id);
            return (
              <span key={id} className="px-2 py-1 bg-muted rounded-md text-xs">
                {mentor?.full_name || id}
              </span>
            );
          })}
        </div>
      )}
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        {value.length > 0 ? 'Ubah Mentor' : 'Pilih Mentor'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pilih Mentor</DialogTitle>
            <DialogDescription>
              Pilih mentor yang berlaku untuk voucher ini.
            </DialogDescription>
          </DialogHeader>

          <Input
            placeholder="Cari mentor..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          <div className="max-h-[300px] overflow-y-auto">
            {mentorsQuery.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="size-6" />
              </div>
            ) : mentors.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Tidak ada mentor ditemukan
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                {mentors.map(mentor => {
                  const checked = value.includes(mentor.id_user);
                  return (
                    <button
                      key={mentor.id_user}
                      type="button"
                      onClick={() => toggle(mentor.id_user)}
                      className="flex items-center gap-2 p-2 rounded-md hover:bg-muted text-left"
                    >
                      <Checkbox
                        checked={checked}
                        onClick={e => e.preventDefault()}
                      />
                      <span className="flex-1 truncate">
                        {mentor.full_name || mentor.id_user}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" onClick={() => setOpen(false)}>
              Selesai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
